import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Briefcase,
  Users,
  Calendar,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Filter,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Reports = () => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');
  const [reportData, setReportData] = useState(null);

  const isCOO = user?.role === 'coo';

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/portal/reports?range=${dateRange}`);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
      // Set mock data for demonstration
      setReportData({
        summary: {
          total_jobs: 24,
          active_jobs: 8,
          completed_jobs: 14,
          jobs_this_period: 6,
          jobs_change: 15.5
        },
        revenue: {
          total: 485000,
          this_period: 125000,
          change: 22.3,
          by_type: [
            { type: 'Kitchen Remodel', amount: 185000, count: 8 },
            { type: 'Bathroom Remodel', amount: 120000, count: 12 },
            { type: 'Whole Home', amount: 95000, count: 2 },
            { type: 'Other', amount: 85000, count: 6 }
          ]
        },
        team: {
          total_members: 12,
          utilization: 78,
          top_performers: [
            { name: 'Mike Johnson', jobs: 8, rating: 4.9 },
            { name: 'Sarah Williams', jobs: 6, rating: 4.8 },
            { name: 'David Lee', jobs: 5, rating: 4.7 }
          ]
        },
        timeline: {
          avg_completion_days: 42,
          on_time_rate: 85,
          delayed_jobs: 2
        },
        monthly_trend: [
          { month: 'Oct', jobs: 5, revenue: 95000 },
          { month: 'Nov', jobs: 4, revenue: 82000 },
          { month: 'Dec', jobs: 6, revenue: 110000 },
          { month: 'Jan', jobs: 7, revenue: 125000 },
          { month: 'Feb', jobs: 8, revenue: 145000 },
          { month: 'Mar', jobs: 6, revenue: 125000 }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const StatCard = ({ title, value, change, icon: Icon, prefix = '', suffix = '' }) => (
    <div className="bg-white border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="w-12 h-12 bg-[#C6A87C]/10 rounded-full flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#C6A87C]" />
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 text-sm font-medium ${
            change >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            {Math.abs(change)}%
          </div>
        )}
      </div>
      <p className="text-gray-500 text-sm uppercase tracking-wider mb-1">{title}</p>
      <p className="font-heading text-3xl">{prefix}{value}{suffix}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-[#C6A87C]" />
            Reports & Analytics
          </h1>
          <p className="text-gray-500 mt-1">
            Performance insights and business metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <button
            onClick={fetchReportData}
            className="p-2 border border-gray-200 hover:border-[#C6A87C] transition-colors"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-gray-800 transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Jobs"
          value={reportData?.summary?.total_jobs || 0}
          change={reportData?.summary?.jobs_change}
          icon={Briefcase}
        />
        <StatCard
          title="Active Jobs"
          value={reportData?.summary?.active_jobs || 0}
          icon={Calendar}
        />
        {isCOO && (
          <StatCard
            title="Revenue"
            value={formatCurrency(reportData?.revenue?.this_period || 0)}
            change={reportData?.revenue?.change}
            icon={DollarSign}
          />
        )}
        <StatCard
          title="Team Utilization"
          value={reportData?.team?.utilization || 0}
          suffix="%"
          icon={Users}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Jobs Trend Chart */}
        <div className="bg-white border border-gray-100 p-6">
          <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#C6A87C]" />
            Jobs Trend
          </h3>
          <div className="h-64 flex items-end gap-2">
            {reportData?.monthly_trend?.map((month, idx) => (
              <div key={idx} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full bg-[#C6A87C] rounded-t transition-all hover:bg-[#B09265]"
                  style={{ height: `${(month.jobs / 10) * 200}px` }}
                  title={`${month.jobs} jobs`}
                />
                <span className="text-xs text-gray-500 mt-2">{month.month}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Revenue by Type (COO only) */}
        {isCOO ? (
          <div className="bg-white border border-gray-100 p-6">
            <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-[#C6A87C]" />
              Revenue by Project Type
            </h3>
            <div className="space-y-4">
              {reportData?.revenue?.by_type?.map((item, idx) => {
                const percentage = (item.amount / reportData.revenue.total) * 100;
                return (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{item.type}</span>
                      <span className="font-medium">{formatCurrency(item.amount)}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 0.5, delay: idx * 0.1 }}
                        className="h-full bg-[#C6A87C]"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">{item.count} projects • {percentage.toFixed(1)}%</p>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 p-6">
            <h3 className="font-heading text-lg mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-[#C6A87C]" />
              Jobs by Status
            </h3>
            <div className="flex items-center justify-center h-56">
              <div className="text-center">
                <div className="relative w-40 h-40 mx-auto">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="80" cy="80" r="60" fill="none" stroke="#f3f4f6" strokeWidth="20" />
                    <circle 
                      cx="80" cy="80" r="60" fill="none" stroke="#C6A87C" strokeWidth="20"
                      strokeDasharray={`${(reportData?.summary?.completed_jobs / reportData?.summary?.total_jobs) * 377} 377`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <p className="text-3xl font-heading">{Math.round((reportData?.summary?.completed_jobs / reportData?.summary?.total_jobs) * 100)}%</p>
                      <p className="text-xs text-gray-500">Completed</p>
                    </div>
                  </div>
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-[#C6A87C] rounded-full" />
                    <span className="text-sm text-gray-600">Completed ({reportData?.summary?.completed_jobs})</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-200 rounded-full" />
                    <span className="text-sm text-gray-600">Active ({reportData?.summary?.active_jobs})</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Details Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline Performance */}
        <div className="bg-white border border-gray-100 p-6">
          <h3 className="font-heading text-lg mb-4">Timeline Performance</h3>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50">
              <p className="text-sm text-gray-500">Avg. Completion Time</p>
              <p className="font-heading text-2xl">{reportData?.timeline?.avg_completion_days} days</p>
            </div>
            <div className="p-4 bg-green-50">
              <p className="text-sm text-gray-500">On-Time Rate</p>
              <p className="font-heading text-2xl text-green-600">{reportData?.timeline?.on_time_rate}%</p>
            </div>
            <div className="p-4 bg-yellow-50">
              <p className="text-sm text-gray-500">Delayed Projects</p>
              <p className="font-heading text-2xl text-yellow-600">{reportData?.timeline?.delayed_jobs}</p>
            </div>
          </div>
        </div>

        {/* Top Performers */}
        <div className="bg-white border border-gray-100 p-6">
          <h3 className="font-heading text-lg mb-4">Top Performers</h3>
          <div className="space-y-3">
            {reportData?.team?.top_performers?.map((member, idx) => (
              <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                  idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : 'bg-amber-700'
                }`}>
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{member.name}</p>
                  <p className="text-sm text-gray-500">{member.jobs} jobs completed</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-[#C6A87C]">{member.rating}</p>
                  <p className="text-xs text-gray-400">rating</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-white border border-gray-100 p-6">
          <h3 className="font-heading text-lg mb-4">Quick Stats</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50">
              <span className="text-gray-600">Team Members</span>
              <span className="font-heading text-xl">{reportData?.team?.total_members}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50">
              <span className="text-gray-600">Jobs This Period</span>
              <span className="font-heading text-xl">{reportData?.summary?.jobs_this_period}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50">
              <span className="text-gray-600">Completed Jobs</span>
              <span className="font-heading text-xl">{reportData?.summary?.completed_jobs}</span>
            </div>
            {isCOO && (
              <div className="flex justify-between items-center p-3 bg-[#C6A87C]/10">
                <span className="text-gray-600">Total Revenue</span>
                <span className="font-heading text-xl text-[#C6A87C]">{formatCurrency(reportData?.revenue?.total || 0)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
